import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton, Chip,
  TextField, MenuItem, Button, Tooltip,
} from '@mui/material';
import { Close, Print, CheckCircle, LockOpen } from '@mui/icons-material';
import {
  patientTherapyPlanService, therapyPlanErrorMessage, TherapyGoalRow,
} from '../../services/patientTherapyPlanService';
import { colors, typography } from '../../theme/designSystem';

/**
 * Detalhe de um objetivo do plano terapêutico (RN-541d). Somente leitura: abre pelo card,
 * tanto na lista quanto no quadro, e mostra por inteiro o que o card corta em duas linhas.
 */
interface Props {
  goal: TherapyGoalRow | null;
  patientId: string;
  onClose: () => void;
  onChanged: () => void;
  /** Reabriu: a tela leva direto aos campos, sem exigir um segundo clique em Editar. */
  onReopened: (goalId: string) => void;
  onError: (mensagem: string) => void;
}

const MAX_PARECER = 2000;

const PRIORIDADE: Record<string, string> = {
  Urgente: '#dc3545',
  Alta: '#fd7e14',
  Média: '#ffc107',
  Baixa: '#6c757d',
};

const formatarData = (iso: string | null): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const Campo: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary, mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
      {label}
    </Typography>
    <Typography component="div" sx={{ fontSize: '0.88rem', color: colors.textPrimary, whiteSpace: 'pre-wrap' }}>
      {children}
    </Typography>
  </Box>
);

const TherapyGoalDetailModal: React.FC<Props> = ({ goal, patientId, onClose, onChanged, onReopened, onError }) => {
  const [concluding, setConcluding] = useState(false);
  const [finalScale, setFinalScale] = useState<number | ''>('');
  const [finalNotes, setFinalNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  if (!goal) return null;

  const pct = (v: number) => `${Math.min(Math.max(v, 0), 10) * 10}%`;
  const concluida = goal.conclusion !== null;

  const concluir = async () => {
    setFormError(null);
    if (finalScale === '') {
      setFormError('Informe a nota final.');
      return;
    }
    if (!finalNotes.trim()) {
      setFormError('Escreva o parecer da conclusão.');
      return;
    }
    setSaving(true);
    try {
      await patientTherapyPlanService.concludeGoal(goal.id, Number(finalScale), finalNotes.trim());
      setConcluding(false);
      setFinalScale('');
      setFinalNotes('');
      onChanged();
      onClose();
    } catch (e) {
      setFormError(therapyPlanErrorMessage(e, 'Não foi possível concluir o objetivo.'));
    } finally {
      setSaving(false);
    }
  };

  const reabrir = async () => {
    try {
      await patientTherapyPlanService.reopenGoal(goal.id);
      onReopened(goal.id);
    } catch (e) {
      onError(therapyPlanErrorMessage(e, 'Não foi possível reabrir o objetivo.'));
    }
  };

  const imprimir = () => {
    window.open(`/?page=therapy-goal-print&goalId=${goal.id}&patientId=${patientId}`, '_blank');
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxHeight: '90vh' } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.primary, color: colors.white, padding: '1.5rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
        }}
      >
        <Typography variant="h6" component="h3" sx={{ fontSize: '1.25rem', fontWeight: typography.fontWeight.semibold, minWidth: 0 }}>
          {goal.title || 'Objetivo sem título'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: colors.white, padding: '0.25rem', flexShrink: 0 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {goal.status && (
            <Chip
              label={goal.status}
              size="small"
              sx={{ height: 24, backgroundColor: colors.backgroundAlt, color: colors.textSecondary }}
            />
          )}
          {goal.priority && (
            <Chip
              label={`Prioridade: ${goal.priority}`}
              size="small"
              sx={{
                height: 24,
                backgroundColor: `${PRIORIDADE[goal.priority] ?? colors.textSecondary}1f`,
                color: PRIORIDADE[goal.priority] ?? colors.textSecondary,
              }}
            />
          )}
          {goal.periodTitle && (
            <Chip label={goal.periodTitle} size="small" sx={{ height: 24, backgroundColor: '#e6f7f9', color: colors.primary }} />
          )}
        </Box>

        {/* A régua, no mesmo desenho do card */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ position: 'relative', height: 10, borderRadius: 5, backgroundColor: '#e9ecef' }}>
            {goal.currentScale !== null && goal.targetScale !== null && goal.targetScale > goal.currentScale && (
              <Box
                sx={{
                  position: 'absolute', left: pct(goal.currentScale), top: 0, bottom: 0,
                  width: `${(goal.targetScale - goal.currentScale) * 10}%`,
                  backgroundColor: 'rgba(3, 180, 198, 0.18)', borderRadius: 5,
                }}
              />
            )}
            {goal.lastEvaluation !== null && (
              <Box
                sx={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, width: pct(goal.lastEvaluation),
                  backgroundColor: colors.primary, borderRadius: 5,
                }}
              />
            )}
            {goal.currentScale !== null && (
              <Box sx={{ position: 'absolute', left: pct(goal.currentScale), top: -3, width: 2, height: 16, backgroundColor: colors.textSecondary }} />
            )}
            {goal.targetScale !== null && (
              <Box sx={{ position: 'absolute', left: pct(goal.targetScale), top: -3, width: 2, height: 16, backgroundColor: colors.success }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            <Typography sx={{ fontSize: '0.82rem', color: colors.textSecondary }}>
              Estado atual: <strong style={{ color: colors.textPrimary }}>{goal.currentScale ?? '—'}</strong>
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: colors.textSecondary }}>
              Meta: <strong style={{ color: colors.success }}>{goal.targetScale ?? '—'}</strong>
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: colors.textSecondary }}>
              Última avaliação:{' '}
              <strong style={{ color: goal.lastEvaluation === null ? colors.textSecondary : colors.primary }}>
                {goal.lastEvaluation ?? 'sem registro'}
              </strong>
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mt: 0.5 }}>
            {goal.evaluationCount === 0
              ? 'Nenhuma sessão avaliou este objetivo. O atingimento é registrado nas evoluções.'
              : `${goal.evaluationCount} sessão(ões) avaliaram · última em ${formatarData(goal.lastEvaluationDate)}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
            {goal.goal && <Campo label="Objetivo">{goal.goal}</Campo>}
            {goal.justify && <Campo label="Justificativa">{goal.justify}</Campo>}
            {goal.metric && <Campo label="Como medir">{goal.metric}</Campo>}
            {goal.notes && <Campo label="Observações">{goal.notes}</Campo>}
          </Box>

          <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
            {goal.professionalNames.length > 0 && (
              <Campo label="Responsável">{goal.professionalNames.join(', ')}</Campo>
            )}
            {goal.teamName && <Campo label="Equipe">{goal.teamName}</Campo>}
            {goal.sessionsQuantity !== null && (
              <Campo label="Sessões previstas">{goal.sessionsQuantity}</Campo>
            )}
            {goal.diagnosisLabel && <Campo label="Diagnóstico (CID)">{goal.diagnosisLabel}</Campo>}
          </Box>
        </Box>

        {/* Conclusão assinada: nota final, parecer e quem respondeu por ele */}
        {concluida && goal.conclusion && (
          <Box sx={{ mt: 1, p: 1.5, borderRadius: '8px', border: `1px solid ${colors.success}`, backgroundColor: '#f2fbf5' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              <CheckCircle sx={{ fontSize: 18, color: colors.success }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: colors.textPrimary }}>
                Objetivo concluído
              </Typography>
              <Chip
                label={`Nota final: ${goal.conclusion.finalScale ?? '—'}`}
                size="small"
                sx={{ height: 22, fontSize: '0.72rem', backgroundColor: '#d4edda', color: '#155724' }}
              />
            </Box>
            <Typography sx={{ fontSize: '0.88rem', color: colors.textPrimary, whiteSpace: 'pre-wrap', mb: 1 }}>
              {goal.conclusion.notes}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary }}>
              {goal.conclusion.professionalName ?? 'Profissional não identificado'}
              {goal.conclusion.professionalDocument && ` · ${goal.conclusion.professionalDocument}`}
              {` · ${formatarData(goal.conclusion.concludedAt)}`}
            </Typography>
          </Box>
        )}

        {/* Formulário de conclusão */}
        {concluding && !concluida && (
          <Box sx={{ mt: 1, p: 1.5, borderRadius: '8px', border: '1px solid #e0e0e0', backgroundColor: colors.background }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: colors.textPrimary, mb: 1.5 }}>
              Concluir objetivo
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              <TextField
                select
                size="small"
                label="Nota final"
                value={finalScale}
                onChange={e => setFinalScale(e.target.value === '' ? '' : Number(e.target.value))}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '0 0 140px' }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </TextField>
              <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary, alignSelf: 'center', flex: '1 1 200px' }}>
                A conclusão sai assinada com seu nome e documento profissional.
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Parecer"
              placeholder="O que foi alcançado e o que sustenta o encerramento"
              value={finalNotes}
              onChange={e => setFinalNotes(e.target.value)}
              inputProps={{ maxLength: MAX_PARECER }}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={3}
            />
            {formError && (
              <Typography sx={{ color: colors.error, fontSize: typography.fontSize.sm, mt: 1 }}>{formError}</Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
        <Tooltip title="Imprimir o plano">
          <Button onClick={imprimir} variant="outlined" startIcon={<Print />} sx={{ textTransform: 'none' }}>
            Imprimir
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {concluida ? (
          <Button onClick={reabrir} variant="outlined" startIcon={<LockOpen />} sx={{ textTransform: 'none' }}>
            Reabrir
          </Button>
        ) : concluding ? (
          <>
            <Button onClick={() => setConcluding(false)} variant="outlined" sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button onClick={concluir} variant="contained" disabled={saving} sx={{ textTransform: 'none' }}>
              Concluir
            </Button>
          </>
        ) : (
          <Button onClick={() => setConcluding(true)} variant="contained" startIcon={<CheckCircle />} sx={{ textTransform: 'none' }}>
            Concluir objetivo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TherapyGoalDetailModal;
